import { apiRequest, useApiQuery, type ApiError } from "@/shared/api";
import type { UseQueryOptions } from "@tanstack/react-query";

export interface Banner {
  imageUrl: string;
  displayOrder: number;
  linkUrl?: string | null;
  title?: string | null;
  description?: string | null;
  isActive?: boolean;
  id?: string;
}

export type BannerResponse = Banner[];
const TRADE_API_ORIGIN = "https://api-trade.nommia.io";

const rewriteBannerImageUrl = (imageUrl: string) => {
  if (imageUrl.startsWith(TRADE_API_ORIGIN)) {
    return imageUrl.replace(TRADE_API_ORIGIN, "/trade-api");
  }

  return imageUrl;
};

const sortBannersByDisplayOrder = (banners: BannerResponse) =>
  [...banners].sort(
    (first, second) => first.displayOrder - second.displayOrder
  );

const bannersRequest = {
  url: "/banners/mobile/course-advert",
  method: "get" as const,
};

export const getBanners = () => apiRequest<BannerResponse>(bannersRequest);

export const useBanners = (
  options?: Omit<
    UseQueryOptions<BannerResponse, ApiError, BannerResponse, ["banners"]>,
    "queryKey" | "queryFn"
  >
) => {
  return useApiQuery<BannerResponse, ApiError, ["banners"]>(
    ["banners"],
    bannersRequest,
    {
      select: (banners) =>
        sortBannersByDisplayOrder(
          banners.map((banner) => ({
            ...banner,
            imageUrl: rewriteBannerImageUrl(banner.imageUrl),
          }))
        ),
      ...options,
    }
  );
};

const upgradeMembershipBannerRequest = {
  url: "/banners/desktop/upgrade-membership",
  method: "get" as const,
};

export const useUpgradeMembershipBanner = (
  options?: Omit<
    UseQueryOptions<
      BannerResponse,
      ApiError,
      BannerResponse,
      ["upgradeMembershipBanner"]
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useApiQuery<BannerResponse, ApiError, ["upgradeMembershipBanner"]>(
    ["upgradeMembershipBanner"],
    upgradeMembershipBannerRequest,
    {
      select: sortBannersByDisplayOrder,
      ...options,
    }
  );
};
